import React from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

export interface Offer {
  id: string | number;
  startDate: string;
  endDate: string;
  configurationType: string;
  creditAmount: number;
  promoLimit: number;
  isAirlineSpecific: boolean;
  airlines?: string[];
}

interface OfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  offer: Offer | null;
  currentIndex: number;
  totalOffers: number;
  onNext: () => void;
  onPrev: () => void;
}

const OfferModal: React.FC<OfferModalProps> = ({
  isOpen,
  onClose,
  offer,
  currentIndex,
  totalOffers,
  onNext,
  onPrev,
}) => {
  const navigate = useNavigate();

  if (!isOpen || !offer) return null;

  const start = offer.startDate && dayjs(offer.startDate);
  const end = offer.endDate && dayjs(offer.endDate);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col md:flex-row relative animate-fadeIn">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
        >
          <i className="st-close text-base"></i>
        </button>

        {/* Left Side - Illustration */}
        <div className="w-full md:w-1/2 bg-[#F0F7FF] flex items-center justify-center p-8 relative overflow-hidden">
            {/* Background decoration similar to image */}
           
             <img 
                src="/images/offer-illustration.png" 
                alt="Travel Offer" 
                className="w-full max-w-[680px] object-contain relative z-10"
             />
        </div>

        {/* Right Side - Content */}
        <div className="w-full md:w-1/2 p-8 flex flex-col">
          {/* Header with Navigation */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-xs font-bold text-light-yellow uppercase tracking-wider">
              Limited-time offers
            </p>
            {totalOffers > 1 && (
                <div className="flex items-center gap-1 bg-gray-50 rounded-full p-1 pl-3 pr-1">
                    <span className="text-xs font-semibold text-gray-500 tracking-wider mr-2">
                        {currentIndex + 1} / {totalOffers}
                    </span>
                    <div className="flex items-center gap-1">
                        <button 
                            onClick={onPrev}
                            className="w-6 h-6 flex items-center justify-center rounded-full bg-white shadow-sm text-gray-400 hover:text-light-yellow hover:scale-105 transition-all"
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        <button 
                            onClick={onNext}
                            className="w-6 h-6 flex items-center justify-center rounded-full bg-white shadow-sm text-gray-400 hover:text-light-yellow hover:scale-105 transition-all"
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
          </div>

          {/* Offer Details */}
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2 mb-2">
                <h2 className="text-xl font-bold text-grease-100 leading-tight">
                {offer.configurationType || "Special Offer"}
                </h2>
                <span className="inline-flex items-center justify-center px-3 py-1 rounded-full border border-light-yellow text-light-yellow text-xs font-bold whitespace-nowrap">
                +{offer.creditAmount} Credits
                </span>
            </div>

            <div className="space-y-3 mt-4">
                {start && end && (
                    <div className="flex items-center gap-2 text-sm text-grease-100">
                        <i className="st-calendar text-gray-400"></i>
                        <span>{start.format("MMM D")} - {end.format("MMM D, YYYY")}</span>
                    </div>
                )}
                
                {offer.promoLimit > 0 && (
                    <div className="flex items-center gap-2 text-sm text-grease-100">
                        <i className="st-user text-gray-400"></i>
                        <span>Limited to the first {offer.promoLimit} signups</span>
                    </div>
                )}

                {offer.isAirlineSpecific && offer.airlines && (
                     <div className="flex items-start gap-2 text-sm text-grease-100">
                        <i className="st-plane text-gray-400 mt-1"></i>
                        <span>Eligible: {offer.airlines.join(", ")}</span>
                    </div>
                )}
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={() => navigate("/signup")}
            className="w-full py-3.5 rounded-xl bg-light-yellow text-white font-semibold text-sm shadow-button hover:bg-[#e6a800] transition-colors mt-8"
          >
            Claim this Offer
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferModal;
